import React from "react";
import {
  Slider,
  Stack,
  Typography,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
} from "@mui/material";
import { SliderQuestion } from "../../types";

interface SliderQuestionProps {
  question: SliderQuestion;
  value: number | string;
  onChange: (value: number | string) => void;
}

const isWeightOrHeightQuestion = (question: SliderQuestion): boolean => {
  const id = question.id.toLowerCase();
  const title = question.title.toLowerCase();
  return (
    id.includes("weight") ||
    id.includes("height") ||
    title.includes("weight") ||
    title.includes("height")
  );
};

const normalizeUnit = (unit: string | null | undefined) =>
  (unit ?? "").trim().toLowerCase();

const formatHeightImperial = (cm: number): string => {
  const totalInches = cm / 2.54;
  let feet = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches - feet * 12);
  if (inches === 12) {
    feet += 1;
    inches = 0;
  }
  return `${feet} ft ${inches} in`;
};

const formatWeightImperial = (kg: number): string => {
  const totalLbs = kg * 2.2046226218;
  let stones = Math.floor(totalLbs / 14);
  let lbs = Math.round(totalLbs - stones * 14);
  if (lbs === 14) {
    stones += 1;
    lbs = 0;
  }
  return `${stones} st ${lbs} lb`;
};

export const SliderQuestionField: React.FC<SliderQuestionProps> = ({
  question,
  value,
  onChange,
}) => {
  const isPreferNotToSay = value === "prefer_not_to_say";
  const showPreferNotToSay = isWeightOrHeightQuestion(question);
  const numericValue =
    typeof value === "number"
      ? value
      : question.defaultValue ?? Math.round((question.min + question.max) / 2);

  const unit = normalizeUnit(question.unit);
  const showImperialConversion =
    typeof numericValue === "number" && (unit === "cm" || unit === "kg");
  const imperialLabel =
    unit === "cm"
      ? formatHeightImperial(numericValue)
      : unit === "kg"
      ? formatWeightImperial(numericValue)
      : "";

  const handlePreferNotToSayChange = (checked: boolean) => {
    if (checked) {
      onChange("prefer_not_to_say");
    } else {
      onChange(numericValue);
    }
  };

  return (
    <Stack spacing={2}>
      {showPreferNotToSay && (
        <Card
          variant="outlined"
          sx={{
            cursor: "pointer",
            borderColor: isPreferNotToSay ? "primary.main" : undefined,
            borderWidth: isPreferNotToSay ? 2 : 1,
            "&:hover": {
              borderColor: "primary.main",
              backgroundColor: "action.hover",
            },
          }}
          onClick={() => handlePreferNotToSayChange(!isPreferNotToSay)}
        >
          <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPreferNotToSay}
                  onChange={(e) => {
                    e.stopPropagation();
                    handlePreferNotToSayChange(e.target.checked);
                  }}
                />
              }
              label={
                <Typography variant="subtitle1">Prefer not to say</Typography>
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePreferNotToSayChange(!isPreferNotToSay);
              }}
              sx={{ m: 0, width: "100%", cursor: "pointer" }}
            />
          </CardContent>
        </Card>
      )}
      {!isPreferNotToSay && (
        <>
          <Typography variant="h3" component="div">
            {numericValue}
            {question.unit ? ` ${question.unit}` : ""}
          </Typography>
          {showImperialConversion && imperialLabel && (
            <Typography variant="h5" fontWeight={600}>
              Imperial: {imperialLabel}
            </Typography>
          )}
          <Slider
            value={numericValue}
            min={question.min}
            max={question.max}
            step={question.step ?? 1}
            onChange={(_, newValue) => {
              if (Array.isArray(newValue)) {
                return;
              }
              onChange(newValue);
            }}
            valueLabelDisplay="auto"
            marks={[
              { value: question.min, label: String(question.min) },
              { value: question.max, label: String(question.max) },
            ]}
          />
        </>
      )}
    </Stack>
  );
};
